import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings: siteSettings } = useSiteSettings();
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/dashboard';

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.username.trim() || !form.email.trim() || !form.password) {
      setError('Please fill in all fields.');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the Terms and Privacy Policy to continue.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);

    // Pre-check: username uniqueness
    const { data: existingUsername } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', form.username.trim())
      .maybeSingle();

    if (existingUsername) {
      setLoading(false);
      setError('That username is already taken. Please choose a different one.');
      return;
    }

    const { error: authError } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        data: {
          username: form.username.trim(),
          role: 'user',
        },
      },
    });
    setLoading(false);
    if (authError) {
      const msg = authError.message.toLowerCase();
      if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('user already exists')) {
        setError('An account with this email already exists. Try signing in instead.');
      } else if (msg.includes('email')) {
        setError('This email address is already in use. Please use a different email.');
      } else {
        setError(authError.message);
      }
      return;
    }
    setRegistered(true);
  };

  const pw = form.password;
  const strength = [
    { label: '6+ chars', ok: pw.length >= 6 },
    { label: 'Uppercase', ok: /[A-Z]/.test(pw) },
    { label: 'Number', ok: /\d/.test(pw) },
  ];

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          {siteSettings.site_logo_url ? (
            <img
              src={siteSettings.site_logo_url}
              alt={siteSettings.site_name || 'Logo'}
              className="h-14 max-w-[200px] mx-auto mb-4 object-contain"
            />
          ) : (
            <img
              src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png"
              alt="API MART"
              className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover"
            />
          )}
          <h1 className="text-2xl font-bold text-white">{siteSettings.site_name || 'API MART'}</h1>
          <p className="text-white/40 text-sm mt-1">ZERO FRICTION. INFINITE TRANSACTIONS.</p>
        </div>

        <div className="bg-white rounded-2xl p-6">
          {registered ? (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-check-line text-green-500 text-3xl" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Check your email!</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                An <strong>Email Activation Link</strong> has been sent to:
              </p>
              <p className="text-sm font-bold text-brand-600 mt-1 mb-3">{form.email}</p>
              <p className="text-xs text-gray-500 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                Kindly click on the link in the email to verify your account and proceed to your dashboard.
                Check your spam folder if you don&apos;t see it.
              </p>
              <button onClick={() => navigate('/login')} className="mt-4 text-xs text-brand-500 hover:underline cursor-pointer">
                Back to Sign In
              </button>
            </motion.div>
          ) : (
          <>
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Create your account</h2>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
            >
              <i className="ri-error-warning-fill text-red-500" />
              {error}
            </motion.div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Username</label>
              <input
                type="text"
                name="username"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                placeholder="yourname"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Email</label>
              <input
                type="email"
                name="email"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
              {pw.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  {strength.map(rule => (
                    <span
                      key={rule.label}
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${rule.ok ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                    >
                      {rule.ok && <i className="ri-check-line mr-0.5" />}{rule.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* T&C Agreement */}
            <div
              className="flex items-start gap-2.5 cursor-pointer"
              onClick={() => setAgreedToTerms(v => !v)}
            >
              <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${agreedToTerms ? 'bg-brand-500 border-brand-500' : 'border-gray-300 bg-white'}`}>
                {agreedToTerms && <i className="ri-check-line text-white text-[10px]" />}
              </div>
              <p className="text-xs text-gray-500 leading-relaxed select-none">
                I agree to API MART&apos;s{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  onClick={e => e.stopPropagation()}
                  className="text-brand-500 hover:underline font-medium"
                >
                  Terms &amp; Conditions
                </Link>
                {' '}and{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  onClick={e => e.stopPropagation()}
                  className="text-brand-500 hover:underline font-medium"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />Creating account...
                </span>
              ) : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-4">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-brand-500 hover:underline cursor-pointer"
            >
              Sign in
            </button>
          </p>
          </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
